// Card geometry, shared by everything that has to agree on where a card sits:
// the renderer, the rule evaluator and the Form Builder canvas.
//
// A card belongs to exactly one CONTAINER — a section body ('order' scope) or
// one repeatable widget node — and inside that container it either sits in the
// body or inside another card. `parent` says which, `order` says where among
// the container's loose fields, on the same scale as a field's sort_order.
import type { FieldDefinition, FormCard, FormSection } from '../shared/types';

/** Cards published before ordering existed sort after everything with an order. */
const LEGACY = Number.MAX_SAFE_INTEGER / 2;

/** Where a card sits among its siblings. */
export const cardOrder = (c: FormCard): number =>
  (typeof c.order === 'number' && Number.isFinite(c.order) ? c.order : LEGACY + (c.sortOrder ?? 0));

/** The cards of a section that belong to one container, in document order. */
export const cardsFor = (section: FormSection, scope: string): FormCard[] =>
  (section.cards || [])
    .filter((c) => (c.scope || 'order') === scope)
    .slice()
    .sort((a, b) => cardOrder(a) - cardOrder(b));

/**
 * A card's parent WITHIN the given list. A parent that is not in the list —
 * deleted, or living in another container — is no parent at all: the card
 * falls back to the container body rather than disappearing with it.
 */
export function parentIn(cards: FormCard[], card: FormCard): string {
  const parent = (card.parent || '').trim();
  if (!parent || parent === card.id) return '';
  return cards.some((c) => c.id === parent) ? parent : '';
}

/** The cards sitting directly inside `parent` ('' = the container's body). */
export const childrenOf = (cards: FormCard[], parent: string): FormCard[] =>
  cards.filter((c) => parentIn(cards, c) === parent);

/**
 * The chain from a card up to the container body — the card itself first.
 * Cycles (only reachable through a hand-edited definition) stop the walk.
 */
export function chainOf(cards: FormCard[], id: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = id;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    out.push(cur);
    const card = cards.find((c) => c.id === cur);
    if (!card) break;
    cur = parentIn(cards, card);
  }
  return out;
}

/** How deep a card sits: 0 in the container body, 1 inside a card, and so on. */
export const depthOfCard = (cards: FormCard[], id: string): number => chainOf(cards, id).length - 1;

/**
 * The card a field is in, as far as THIS container is concerned. A group id no
 * card in the container claims reads as the body — a field must never vanish
 * because the card that grouped it was deleted.
 */
export function groupIn(cards: FormCard[], f: FieldDefinition): string {
  const group = (f.ui_meta?.group || '').trim();
  return group && cards.some((c) => c.id === group) ? group : '';
}
